import { createContext, useContext, useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";

// Scroll-reveal wrappers built on CSS transitions + IntersectionObserver.
// These used to be motion/react components; that put ~117 KiB of animation runtime in the
// main bundle (Home imports them) for effects plain CSS handles.

interface AnimationProps {
  children: ReactNode;
  className?: string;
  delay?: number;
  duration?: number;
  threshold?: number;
  once?: boolean;
}

const prefersReducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

function useInView<T extends Element>(threshold: number, once: boolean) {
  const ref = useRef<T>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          if (once) io.disconnect();
        } else if (!once) {
          setInView(false);
        }
      },
      { threshold },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold, once]);

  return [ref, inView] as const;
}

function revealStyle(shown: boolean, hiddenTransform: string, duration: number, delay: number): CSSProperties {
  if (prefersReducedMotion()) return {};
  const timing = `${duration}s ease-out ${delay}s`;
  return {
    opacity: shown ? 1 : 0,
    transform: shown ? "none" : hiddenTransform,
    transition: `opacity ${timing}, transform ${timing}`,
  };
}

/** Fade in from transparent */
export function FadeIn({ children, className, delay = 0, duration = 0.5, threshold = 0.2, once = true }: AnimationProps) {
  const [ref, inView] = useInView<HTMLDivElement>(threshold, once);
  return (
    <div ref={ref} className={className} style={revealStyle(inView, "none", duration, delay)}>
      {children}
    </div>
  );
}

/** Slide up from below + fade */
export function SlideUp({
  children,
  className,
  delay = 0,
  duration = 0.6,
  distance = 30,
  threshold = 0.2,
  once = true,
}: AnimationProps & { distance?: number }) {
  const [ref, inView] = useInView<HTMLDivElement>(threshold, once);
  return (
    <div ref={ref} className={className} style={revealStyle(inView, `translateY(${distance}px)`, duration, delay)}>
      {children}
    </div>
  );
}

/** Scale in from slightly smaller + fade */
export function ScaleIn({ children, className, delay = 0, duration = 0.5, threshold = 0.2, once = true }: AnimationProps) {
  const [ref, inView] = useInView<HTMLDivElement>(threshold, once);
  return (
    <div ref={ref} className={className} style={revealStyle(inView, "scale(0.9)", duration, delay)}>
      {children}
    </div>
  );
}

const StaggerContext = createContext({ inView: true, delay: 0, staggerDelay: 0 });

/** Container that staggers its children's entrance */
export function StaggerContainer({
  children,
  className,
  delay = 0,
  staggerDelay = 0.1,
  threshold = 0.1,
  once = true,
}: Omit<AnimationProps, "duration"> & { staggerDelay?: number }) {
  const [ref, inView] = useInView<HTMLDivElement>(threshold, once);
  return (
    <StaggerContext.Provider value={{ inView, delay, staggerDelay }}>
      <div ref={ref} className={className}>
        {children}
      </div>
    </StaggerContext.Provider>
  );
}

/** Individual child of StaggerContainer; its delay comes from its position among its siblings. */
export function StaggerItem({ children, className, duration = 0.5 }: { children: ReactNode; className?: string; duration?: number }) {
  const { inView, delay, staggerDelay } = useContext(StaggerContext);
  const ref = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(0);

  useLayoutEffect(() => {
    const el = ref.current;
    if (el?.parentElement) setIndex(Array.prototype.indexOf.call(el.parentElement.children, el));
  }, []);

  return (
    <div ref={ref} className={className} style={revealStyle(inView, "translateY(24px)", duration, delay + index * staggerDelay)}>
      {children}
    </div>
  );
}
